import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { STORAGE_KEY, readStoredTheme } from './storage';
import { ThemeContext, type Theme } from './ThemeContext';

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * App-wide light/dark theme state — an explicit user choice, not a mirror of the OS setting.
 *
 * The OS/browser `prefers-color-scheme` is consulted only once, as the INITIAL default when no choice has
 * been saved yet (see `readStoredTheme`); after that, this provider never re-reads it, so a user's saved
 * preference is never silently overridden by a later OS theme change. The blocking inline script in
 * index.html reads the same `localStorage` key and sets the same `data-theme` attribute before React
 * mounts, so the value read here on first render always matches what was already painted — no flash.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Private-browsing/storage-disabled: the choice still applies for this page load via React state
      // and the DOM attribute, it just won't persist to the next visit.
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
